import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { EMPTY_LIST } from '../../../../../../../constants/common'
import { useTimeZone } from '../../../../../../../hooks'
import { LISTING_PROFILE_EDIT_BLOCK } from '../../../RentalPortalListingProfile.constants'
import {
  useListingProfileContext,
  useListingProfileEditBlockOpener,
  useListingProfilePhotoEditorOpener,
} from '../../../RentalPortalListingProfile.hooks'
import { LISTING_READINESS_KEY } from '../../RentalPortalListingProfileWidgetPublishing.constants'
import {
  getIsListingReadinessCollapsedByDefault,
  getIsListingReadinessExpanded,
  getListingManagePublishingState,
  getListingReadinessRows,
  getListingReadinessSummary,
  getListingScheduleRefusalText,
} from '../../RentalPortalListingProfileWidgetPublishing.utils'

export function useListingProfileWidgetPublishing() {
  const { t } = useTranslation()

  const navigate = useNavigate()

  const timezone = useTimeZone()

  const [manualChoice, setManualChoice] = useState(null)

  const { isInitiated, listingData } = useListingProfileContext()

  const {
    canEditPhotos,
    handleOpenPhotoEditor,
    isPhotoEditorOpen,
    photoEditorProps,
  } = useListingProfilePhotoEditorOpener()

  const { canEditBlocks, handleOpenEditBlock } =
    useListingProfileEditBlockOpener()

  const readinessChecks = listingData?.publish_readiness ?? EMPTY_LIST

  const { doneCount, isComplete, total } =
    getListingReadinessSummary(readinessChecks)

  const isExpanded = getIsListingReadinessExpanded({
    isCollapsedByDefault: getIsListingReadinessCollapsedByDefault({
      isReadinessComplete: isComplete,
      listingData,
    }),
    isReadinessComplete: isComplete,
    listingId: listingData?.id,
    manualChoice,
  })

  const {
    isDisabled: isManageDisabled,
    tooltipKey: manageTooltipKey,
    url: manageUrl,
  } = getListingManagePublishingState(listingData)

  function handleToggleReadiness() {
    if (isComplete) {
      setManualChoice({ isExpanded: !isExpanded, listingId: listingData?.id })
    }
  }

  function handleClickManagePublishing() {
    navigate(manageUrl)
  }

  function handleRequestDescriptionEdit() {
    handleOpenEditBlock(LISTING_PROFILE_EDIT_BLOCK.description)
  }

  function handleRequestKeyFactsEdit() {
    handleOpenEditBlock(LISTING_PROFILE_EDIT_BLOCK.keyFacts)
  }

  return {
    doneCount,
    handleClickManagePublishing,
    handleToggleReadiness,
    isComplete,
    isExpanded,
    isManageDisabled,
    isPhotoEditorOpen,
    isSkeleton: !isInitiated || !listingData,
    listingStatus: listingData?.status,
    manageTooltipKey,
    photoEditorProps,
    readinessFixHandlers: {
      [LISTING_READINESS_KEY.description]: handleRequestDescriptionEdit,
      [LISTING_READINESS_KEY.photo]: handleOpenPhotoEditor,
      [LISTING_READINESS_KEY.portal]: handleClickManagePublishing,
      [LISTING_READINESS_KEY.rent]: handleRequestKeyFactsEdit,
    },
    readinessRows: getListingReadinessRows({
      canEdit: canEditBlocks,
      canEditPhotos,
      canViewPublications: Boolean(
        listingData?.permissions?.can_view_publications
      ),
      checks: readinessChecks,
    }),
    scheduleRefusalText: getListingScheduleRefusalText({
      checks: readinessChecks,
      refusal: listingData?.schedule_refusal,
      t,
      timezone,
    }),
    total,
  }
}
